/**
 * Shared geometry for the CEC structure / cation-balance triangle.
 *
 * Methodology (Phase 10A.7 — CEC saturation residual convention):
 *   Ca-sat (%)   = Ca / CEC × 100
 *   Mg-sat (%)   = Mg / CEC × 100
 *   Residual (%) = max(0, 100 − Ca-sat − Mg-sat)  // K + Na + acidity etc.
 *
 * Apexes match the historical Structure Triangle asset (viewBox 0 0 842 842):
 *   Ca       top
 *   Residual bottom-left
 *   Mg       bottom-right
 *
 * Zone path data: `structureTriangleZones.ts` (exact asset geometry).
 * Zone colours: `chartTheme.structure*` (editable, Flaha workspace rhythm).
 */
import type { StructureAnalysisBlock } from "@flaha/shared-types";

export const STRUCTURE_SVG_SIZE = 842;

export const APEX_CA = { x: 408.28, y: 135.9 } as const;
export const APEX_RESIDUAL = { x: 43.87, y: 562.74 } as const;
export const APEX_MG = { x: 780.58, y: 562.74 } as const;

export interface CecSaturation {
	caSat: number;
	mgSat: number;
	residual: number;
}

export function computeCecSaturation(
	s: StructureAnalysisBlock
): CecSaturation | null {
	if (s.cec == null || !Number.isFinite(s.cec) || s.cec <= 0) return null;
	const ca = Number.isFinite(s.ca) ? Math.max(0, s.ca) : 0;
	const mg = Number.isFinite(s.mg) ? Math.max(0, s.mg) : 0;
	const caSat = (ca / s.cec) * 100;
	const mgSat = (mg / s.cec) * 100;
	const residual = Math.max(0, 100 - caSat - mgSat);
	return { caSat, mgSat, residual };
}

/**
 * Barycentric placement on the Ca / Residual / Mg triangle.
 * Weights are normalized so Ca+Mg+Residual map into the triangle even when
 * floating-point noise leaves a residual sum slightly off 100.
 */
export function barycentricToStructureSvg(
	caSat: number,
	mgSat: number,
	residual: number
): { x: number; y: number } {
	const sum = caSat + mgSat + residual;
	if (sum <= 0) {
		return {
			x: (APEX_CA.x + APEX_MG.x + APEX_RESIDUAL.x) / 3,
			y: (APEX_CA.y + APEX_MG.y + APEX_RESIDUAL.y) / 3,
		};
	}
	const a = caSat / sum;
	const m = mgSat / sum;
	const r = residual / sum;
	return {
		x: a * APEX_CA.x + m * APEX_MG.x + r * APEX_RESIDUAL.x,
		y: a * APEX_CA.y + m * APEX_MG.y + r * APEX_RESIDUAL.y,
	};
}

/** True when the sample point lies inside (or on) the triangle outline. */
export function pointInsideStructureTriangle(x: number, y: number): boolean {
	const x1 = APEX_CA.x;
	const y1 = APEX_CA.y;
	const x2 = APEX_RESIDUAL.x;
	const y2 = APEX_RESIDUAL.y;
	const x3 = APEX_MG.x;
	const y3 = APEX_MG.y;
	const denom = (y2 - y3) * (x1 - x3) + (x3 - x2) * (y1 - y3);
	if (Math.abs(denom) < 1e-9) return false;
	const a = ((y2 - y3) * (x - x3) + (x3 - x2) * (y - y3)) / denom;
	const b = ((y3 - y1) * (x - x3) + (x1 - x3) * (y - y3)) / denom;
	const c = 1 - a - b;
	const eps = 1e-6;
	return a >= -eps && b >= -eps && c >= -eps;
}

/**
 * Edge tick positions matching the original diagram’s scale labels.
 * Left edge = Ca vs Residual (Mg=0); right edge = Ca vs Mg (Residual=0).
 */
export function edgeTick(
	edge: "left" | "right" | "base",
	/** For left/right: Ca-sat %; for base: residual % from residual apex toward Mg. */
	value: number
): { x: number; y: number } {
	if (edge === "left") {
		return barycentricToStructureSvg(value, 0, 100 - value);
	}
	if (edge === "right") {
		return barycentricToStructureSvg(value, 100 - value, 0);
	}
	// base: ca=0, residual = value, mg = 100-value
	return barycentricToStructureSvg(0, 100 - value, value);
}
